import { useEffect, useRef } from 'react';
import {
  OptiSignsBridge,
  CHILD_ACTION,
  setActiveBridge,
  type SyncThemePayload,
} from './OptiSignsBridge';
import { useEmbedStore } from '@/stores/embed-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useTheme } from '@/contexts/theme-context';
import { auth } from '@/lib/firebase';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * React hook that initializes the OptiSignsBridge when in embed mode.
 *
 * Uses the standard scio3 bridge protocol (same as Designer/scio1):
 * - Child → Parent: JSON.stringify({ action, requestId, data })
 * - Parent → Child: JSON.stringify({ action: 'responsePromise', requestId, data })
 * - Parent push:    JSON.stringify({ action, data })        (no requestId)
 *
 * Auth flow:
 * 1. ON_INIT sent → parent responds with { sessionId, teamId, teamName, theme, role }
 * 2. We decode sessionId to get Firebase ID token
 * 3. Call /api/auth/join with token + role to sync user
 * 4. On every API call, auth.getIdToken() → bridge.sendRequest('ON_GET_TOKEN')
 *    → parent responds with { sessionId } → we decode and return the token
 *
 * Theme sync:
 * - ON_INIT response includes current resolved theme → applied immediately
 * - Parent pushes SYNC_THEME when user toggles theme → applied via setTheme
 */
export function useOptiSignsBridge(): OptiSignsBridge | null {
  const bridgeRef = useRef<OptiSignsBridge | null>(null);
  const isEmbedded = useEmbedStore((s) => s.isEmbedded);
  const { setTheme } = useTheme();

  // Keep setTheme in a ref so the bridge handler always has the latest
  const setThemeRef = useRef(setTheme);
  setThemeRef.current = setTheme;

  useEffect(() => {
    if (!isEmbedded) return;

    const bridge = new OptiSignsBridge();
    bridgeRef.current = bridge;
    setActiveBridge(bridge);

    // Listen for SYNC_THEME pushes from parent
    bridge.on('SYNC_THEME', (data) => {
      const payload = data as SyncThemePayload | undefined;
      if (payload?.theme === 'light' || payload?.theme === 'dark') {
        setThemeRef.current(payload.theme);
      }
    });

    // Listen for WORKSPACE_READY push — scio3 sends this after provisioning succeeds.
    // Only update workspace store here. Do NOT clear needsProvisioning — that must
    // happen in EmbedOnboarding.handleCreate AFTER it writes the prompt to the URL
    // via history.replaceState. Clearing it here would unmount EmbedOnboarding before
    // the PROVISION_WORKSPACE response arrives, losing the prompt.
    bridge.on('WORKSPACE_READY', (data) => {
      const payload = data as { workspaceId?: string } | undefined;
      if (payload?.workspaceId) {
        useWorkspaceStore.getState().setActiveWorkspaceId(payload.workspaceId);
      }
    });

    // Start listening for parent messages
    bridge.init();

    // Send ON_INIT request — parent responds with { sessionId, teamId, teamName, theme }
    // Same pattern as SmallApp ON_INIT → sendTokenNew(requestId)
    (async () => {
      try {
        const response = await bridge.sendRequest<{
          sessionId?: string;
          teamId?: string;
          teamName?: string;
          workspaceId?: string;
          theme?: 'light' | 'dark';
          role?: string;
        }>(CHILD_ACTION.ON_INIT);

        // Apply theme from parent immediately
        if (response?.theme === 'light' || response?.theme === 'dark') {
          setThemeRef.current(response.theme);
        }

        if (response?.sessionId) {
          // Decode sessionId: base64 → JSON { token, expires, isFBToken, uid }
          const decoded = JSON.parse(atob(response.sessionId));
          const token = decoded.token;

          // Store token for initial /api/auth/join call
          useEmbedStore.getState().setParentToken(token);

          // Sync with OptiDev backend. source='optisigns' + role lets the backend
          // check access: owner/admin/superadmin are allowed, others get needs_admin.
          const res = await fetch(`${API_URL}/api/auth/join`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ source: 'optisigns', role: response?.role, workspaceId: response?.workspaceId }),
          });

          if (res.ok) {
            const data = await res.json();
            if (data.needs_admin) {
              useEmbedStore.getState().setNeedsAdminAccess(true);
            } else {
              auth.currentUser = data.user;
              window.dispatchEvent(new CustomEvent('embed-auth', { detail: data.user }));
            }
          } else {
            console.error('[OptiSignsBridge] Backend sync failed:', res.status);
          }
        }

        // If parent reports no workspaceId, the account has no OptiDev workspace yet.
        // Show EmbedOnboarding so the admin can provision one.
        if (!response?.workspaceId) {
          useEmbedStore.getState().setNeedsProvisioning(true);
        }

        // Workspace ID is set from ?ws=<uuid> URL param (workspace-store.ts)
        // Validate it matches what parent sent
        const wsFromUrl = new URLSearchParams(window.location.search).get('ws');
        if (response?.workspaceId && response.workspaceId !== wsFromUrl) {
          console.warn('[OptiSignsBridge] Workspace mismatch — URL ws:', wsFromUrl, 'parent workspaceId:', response.workspaceId);
        }
      } catch (err) {
        console.error('[OptiSignsBridge] Init failed:', err);
      }
    })();

    return () => {
      bridge.destroy();
      bridgeRef.current = null;
      setActiveBridge(null);
    };
  }, [isEmbedded]);

  return bridgeRef.current;
}
